import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', amber: '#F0B429', text: '#E8EDF5', sub: '#C5CDD8',
};

export default function PaymentVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [plan, setPlan] = useState('');
  const [saleSummary, setSaleSummary] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const flow = searchParams.get('flow') || 'subscription';
  const reference = searchParams.get('reference') || searchParams.get('trxref');

  useEffect(() => {
    if (!reference) {
      setStatus('error');
      setErrorMsg('No payment reference found in URL.');
      return;
    }
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, flow]);

  const verify = async () => {
    setStatus('verifying');
    try {
      if (flow === 'sale') {
        const { data } = await api.post('/payments/verify', { reference });
        if (data.status === 'processing') {
          setTimeout(() => verify(), 1500);
          return;
        }
        setSaleSummary(data.sale);
        setStatus('success');

        localStorage.setItem('optika:last-sale-payment', JSON.stringify({
          reference,
          status: 'paid',
          sale: data.sale,
          at: Date.now(),
        }));

        setTimeout(() => navigate('/pos'), 3000);
        return;
      }

      const { data } = await api.post('/subscriptions/verify', { reference });
      setPlan(data.plan);
      setStatus('success');

      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      if (token) {
        const userRes = await api.get('/subscriptions/status').catch(() => null);
        if (userRes) {
          const stored = JSON.parse(localStorage.getItem('user') || '{}');
          const updated = {
            ...stored,
            plan: data.plan,
            subscriptionStatus: userRes.data.subscriptionStatus,
            subscriptionEndDate: userRes.data.subscriptionEndDate,
          };
          localStorage.setItem('user', JSON.stringify(updated));
          if (refreshToken) login(token, refreshToken, updated);
        }
      }

      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'Payment verification failed. Please contact support.');
      setStatus('error');
    }
  };

  const PLAN_LABEL = { pro: 'Pro', business: 'Business' };
  const paymentLabel = {
    cash: 'Cash',
    momo: 'Mobile Money (MoMo)',
    card: 'Card',
    credit: 'Credit',
  };
  const settlementLabel = {
    paid: 'Paid in full',
    partial: 'Part paid',
    credit: 'On credit',
  };

  const primaryBtn = { background: C.gold, color: C.ink, border: 'none', borderRadius: 10, padding: '11px 28px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', fontFamily: 'inherit' };
  const rowLabel = { color: C.muted };
  const rowValue = { fontWeight: 700, color: C.text };

  return (
    <div style={{ minHeight: '100vh', background: C.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Cabinet Grotesk', sans-serif", padding: 24 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, padding: '48px 40px', maxWidth: 460, width: '100%', textAlign: 'center', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ marginBottom: 32 }}>
          <span style={{ fontSize: '1.3rem', fontWeight: 800, color: C.text }}>OptiKa</span>
        </div>

        {status === 'verifying' && (
          <>
            <div style={{ width: 56, height: 56, border: `4px solid ${C.border}`, borderTopColor: C.gold, borderRadius: '50%', margin: '0 auto 24px', animation: 'spin 0.8s linear infinite' }} />
            <h2 style={{ margin: '0 0 8px', color: C.text, fontSize: '1.25rem', fontWeight: 800 }}>Verifying payment...</h2>
            <p style={{ margin: 0, color: C.muted, fontSize: '0.9rem' }}>
              Please wait while we confirm your {flow === 'sale' ? 'customer payment' : 'subscription transaction'}.
            </p>
          </>
        )}

        {status === 'success' && flow === 'sale' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,201,177,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '2rem', color: C.teal }}>
              ✓
            </div>
            <h2 style={{ margin: '0 0 8px', color: C.teal, fontSize: '1.4rem', fontWeight: 800 }}>Payment confirmed</h2>
            <p style={{ margin: '0 0 16px', color: C.sub, fontSize: '0.95rem' }}>
              This sale has been marked as paid in OptiKa.
            </p>
            {saleSummary && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 20, textAlign: 'left' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 8 }}>
                  <span style={rowLabel}>Sale</span>
                  <span style={rowValue}>#{String(saleSummary.id).padStart(4, '0')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 8 }}>
                  <span style={rowLabel}>Payment method</span>
                  <span style={rowValue}>
                    {saleSummary.settlementStatus === 'partial'
                      ? `${paymentLabel[saleSummary.paymentMethod] || saleSummary.paymentMethod} + Credit`
                      : paymentLabel[saleSummary.paymentMethod] || saleSummary.paymentMethod}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 8 }}>
                  <span style={rowLabel}>Settlement</span>
                  <span style={rowValue}>{settlementLabel[saleSummary.settlementStatus] || saleSummary.settlementStatus || 'Paid'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 8 }}>
                  <span style={rowLabel}>Subtotal</span>
                  <span style={rowValue}>GHS {Number(saleSummary.subtotal || saleSummary.total || 0).toFixed(2)}</span>
                </div>
                {Number(saleSummary.discountAmount || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 8 }}>
                    <span style={rowLabel}>Discount</span>
                    <span style={{ fontWeight: 700, color: C.amber }}>− GHS {Number(saleSummary.discountAmount || 0).toFixed(2)}</span>
                  </div>
                )}
                {Number(saleSummary.loyaltyDiscount || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 8 }}>
                    <span style={rowLabel}>Loyalty discount</span>
                    <span style={{ fontWeight: 700, color: C.amber }}>− GHS {Number(saleSummary.loyaltyDiscount || 0).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 8 }}>
                  <span style={rowLabel}>Sale total</span>
                  <span style={rowValue}>GHS {Number(saleSummary.total || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: Number(saleSummary.balanceDue || 0) > 0 ? 8 : 0 }}>
                  <span style={rowLabel}>Collected now</span>
                  <span style={rowValue}>GHS {Number(saleSummary.amountCollected || 0).toFixed(2)}</span>
                </div>
                {Number(saleSummary.balanceDue || 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem' }}>
                    <span style={rowLabel}>Balance due</span>
                    <span style={{ fontWeight: 700, color: C.coral }}>GHS {Number(saleSummary.balanceDue || 0).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
            {saleSummary?.settlementStatus === 'partial' && (
              <div style={{ background: 'rgba(240,180,41,0.08)', border: '1px solid rgba(240,180,41,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 20 }}>
                <p style={{ margin: 0, fontSize: '0.82rem', color: C.gold, lineHeight: 1.5 }}>
                  The sale is complete and the remaining balance has been added to customer collections in OptiKa.
                </p>
              </div>
            )}
            <div style={{ background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: C.teal }}>Returning to POS in 3 seconds...</p>
            </div>
            <button onClick={() => navigate('/pos')} style={primaryBtn}>
              Return to POS now
            </button>
          </>
        )}

        {status === 'success' && flow !== 'sale' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,201,177,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '2rem', color: C.teal }}>
              ✓
            </div>
            <h2 style={{ margin: '0 0 8px', color: C.teal, fontSize: '1.4rem', fontWeight: 800 }}>Plan upgraded!</h2>
            <p style={{ margin: '0 0 20px', color: C.sub, fontSize: '0.95rem' }}>
              You are now on the <strong>{PLAN_LABEL[plan] || plan}</strong> plan.
            </p>
            <div style={{ background: 'rgba(0,201,177,0.08)', border: '1px solid rgba(0,201,177,0.25)', borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: C.teal }}>Redirecting to dashboard in 3 seconds...</p>
            </div>
            <button onClick={() => navigate('/')} style={primaryBtn}>
              Go to Dashboard now
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,107,107,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '2rem', color: C.coral }}>
              ✕
            </div>
            <h2 style={{ margin: '0 0 8px', color: C.coral, fontSize: '1.25rem', fontWeight: 800 }}>Verification failed</h2>
            <p style={{ margin: '0 0 24px', color: C.sub, fontSize: '0.9rem' }}>{errorMsg}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={verify} style={{ ...primaryBtn, padding: '10px 22px', fontSize: '0.875rem' }}>
                Retry
              </button>
              <button
                onClick={() => navigate(flow === 'sale' ? '/pos' : '/pricing')}
                style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 22px', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem', fontFamily: 'inherit' }}
              >
                {flow === 'sale' ? 'Back to POS' : 'Back to Pricing'}
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
